<script setup lang="ts">
import { useIntersectionAnimation } from "@/composables/use-intersection-animation";
import type { CandidatesPlatformFeatures } from "@/types/marketing";

interface Props {
  content: CandidatesPlatformFeatures;
}

defineProps<Props>();
const { elementRef } = useIntersectionAnimation({ threshold: 0.1 });
</script>

<template>
  <section class="relative py-16 lg:py-24 2xl:py-32 3xl:py-40 4xl:py-48 bg-warm-white overflow-hidden">
    <div ref="elementRef" class="max-w-[1400px] 2xl:max-w-[1600px] 3xl:max-w-[1800px] 4xl:max-w-[2200px] mx-auto px-4 sm:px-6 lg:px-8 2xl:px-12 3xl:px-16 4xl:px-20">
      <!-- Section Header -->
      <div class="text-center max-w-3xl 2xl:max-w-4xl 3xl:max-w-5xl mx-auto mb-12 lg:mb-16 2xl:mb-20 fade-up">
        <div class="badge-hospitality inline-flex mb-6 2xl:mb-8 2xl:text-base 3xl:text-lg 4xl:text-xl">
          <span class="badge-hospitality-text">{{ content.sectionSubtitle }}</span>
        </div>
        <h2 class="font-secondary text-3xl md:text-4xl lg:text-5xl 2xl:text-6xl 3xl:text-7xl 4xl:text-8xl font-bold tracking-tight text-dark-gray mb-6 2xl:mb-8 !pt-0 !pb-0">
          {{ content.sectionTitle }}
        </h2>
        <p class="text-lg 2xl:text-xl 3xl:text-2xl 4xl:text-3xl text-medium-gray">
          {{ content.sectionDescription }}
        </p>
      </div>

      <!-- Features Grid -->
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 2xl:gap-8 3xl:gap-10">
        <div
          v-for="(feature, index) in content.features"
          :key="feature.title"
          class="group relative bg-white rounded-2xl 2xl:rounded-3xl p-6 2xl:p-8 3xl:p-10 shadow-warm hover:shadow-warm-lg transition-all duration-300 hover:-translate-y-1 fade-up"
          :style="{ '--animation-delay': `${index * 100}ms` }"
        >
          <!-- Icon -->
          <div class="w-14 h-14 2xl:w-16 2xl:h-16 3xl:w-20 3xl:h-20 4xl:w-24 4xl:h-24 rounded-2xl 2xl:rounded-3xl bg-gradient-to-br from-primary/10 to-savanna/10 flex items-center justify-center mb-5 2xl:mb-6 group-hover:from-primary/20 group-hover:to-savanna/20 transition-colors">
            <Icon :name="feature.icon" class="w-7 h-7 2xl:w-8 2xl:h-8 3xl:w-10 3xl:h-10 4xl:w-12 4xl:h-12 text-primary" />
          </div>

          <!-- Title -->
          <h3 class="font-secondary text-xl 2xl:text-2xl 3xl:text-3xl 4xl:text-4xl font-bold text-dark-gray mb-3 2xl:mb-4">
            {{ feature.title }}
          </h3>

          <!-- Description -->
          <p class="text-medium-gray 2xl:text-lg 3xl:text-xl 4xl:text-2xl mb-5 2xl:mb-6">
            {{ feature.description }}
          </p>

          <!-- Highlights -->
          <ul class="space-y-2 2xl:space-y-3">
            <li
              v-for="highlight in feature.highlights"
              :key="highlight"
              class="flex items-start gap-2 2xl:gap-3"
            >
              <Icon name="material-symbols:check-circle" class="w-5 h-5 2xl:w-6 2xl:h-6 3xl:w-7 3xl:h-7 text-savanna flex-shrink-0 mt-0.5" />
              <span class="text-sm 2xl:text-base 3xl:text-lg 4xl:text-xl text-dark-gray">{{ highlight }}</span>
            </li>
          </ul>

          <!-- Subtle gradient border on hover -->
          <div class="absolute inset-0 rounded-2xl 2xl:rounded-3xl border-2 border-transparent group-hover:border-savanna/20 transition-colors pointer-events-none"></div>
        </div>
      </div>
    </div>

    <!-- Background decorative elements -->
    <div class="absolute top-1/4 -left-24 w-72 h-72 2xl:w-96 2xl:h-96 3xl:w-[450px] 3xl:h-[450px] bg-primary/5 rounded-full blur-3xl pointer-events-none"></div>
    <div class="absolute bottom-1/4 -right-24 w-64 h-64 2xl:w-80 2xl:h-80 3xl:w-96 3xl:h-96 bg-savanna/5 rounded-full blur-3xl pointer-events-none"></div>
  </section>
</template>
